import { useId } from 'react'

import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'

export function AppraisalField({
  id,
  label,
  description,
  helperText,
  error,
  value,
  defaultValue,
  onChange,
  prefix,
  suffix,
  type = 'text',
  className,
  isLabelHidden = false,
  ...inputProps
}) {
  const generatedId = useId()
  const inputId = id ?? generatedId
  const unitText = [
    prefix ? `Input prefix: ${prefix}.` : null,
    suffix ? `Input suffix: ${suffix}.` : null,
  ].filter(Boolean).join(' ')
  const fieldDescription = [
    description,
    helperText,
    error,
    unitText,
  ].filter(Boolean).join(' ')
  const descriptionId = fieldDescription ? `${inputId}-description` : undefined
  const {
    'aria-describedby': ariaDescribedBy,
    ...forwardedInputProps
  } = inputProps
  const inputDescriptionId = [
    ariaDescribedBy,
    descriptionId,
  ].filter(Boolean).join(' ') || undefined
  const valueProps = value === undefined ? { defaultValue } : { value }

  return (
    <div className="space-y-1.5">
      <Label
        htmlFor={inputId}
        className={cn(
          isLabelHidden
            ? 'sr-only'
            : 'text-[10px] font-bold uppercase tracking-wider text-muted-foreground',
        )}
      >
        {label}
      </Label>
      <div className="relative">
        {prefix ? (
          <span
            aria-hidden="true"
            className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold text-muted-foreground"
          >
            {prefix}
          </span>
        ) : null}
        <Input
          {...forwardedInputProps}
          {...valueProps}
          id={inputId}
          type={type}
          onChange={onChange}
          aria-invalid={Boolean(error)}
          aria-describedby={inputDescriptionId}
          className={cn(prefix && 'pl-7', suffix && 'pr-8', className)}
        />
        {suffix ? (
          <span
            aria-hidden="true"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-bold text-muted-foreground"
          >
            {suffix}
          </span>
        ) : null}
      </div>
      {error || helperText ? (
        <p
          id={descriptionId}
          role={error ? 'alert' : undefined}
          className={cn('text-xs font-medium', error ? 'text-destructive' : 'text-muted-foreground')}
        >
          {error ?? helperText}
        </p>
      ) : fieldDescription ? (
        <p id={descriptionId} className="sr-only">{fieldDescription}</p>
      ) : null}
    </div>
  )
}
